import React, { ChangeEvent } from 'react';
import { useLocation } from '@reach/router';
import { ErrorMessage, Field } from 'formik';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled, { css } from 'styled-components';
import FormFieldError from '@components/shared/Form/FormFieldError';
import { CheckboxDesign } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import ExclamationPurpleIcon from '@images/vms-account/vms-icons/ExclamationPurple.svg';
import { replaceText } from '@utils/handleTextReplacement';
import { getTranslatedHtml } from '@utils/html';
import Colors from '../Colors';
import DidYouKnow from '../design/DidYouKnow';
import { TextVariant, Typography } from '../design/Typography';
import IconWithTooltip from '../IconWithTooltip';
import CustomCheckbox from './CustomCheckbox';

interface Props {
  label: string;
  id?: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<any>) => void;
  tooltip?: string;
  checked: boolean;
  disabled?: boolean;
  design?: CheckboxDesign;
  showError?: boolean;
  redFlag?: string;
}

const Checkbox: React.FC<Props> = ({
  label,
  id,
  name,
  value,
  onChange,
  tooltip,
  checked,
  disabled,
  design = CheckboxDesign.Classic,
  showError = true,
  redFlag
}) => {
  const { t } = useTranslation();
  const { country } = useCountry();
  const location = useLocation();
  const labelText = getTranslatedHtml(replaceText(label, country, location));

  const renderTooltip = () => (
    <TooltipWrap>
      <IconWithTooltip text={replaceText(tooltip, country, location)} />
    </TooltipWrap>
  );

  return (
    <Container>
      <CheckboxWrap $disabled={disabled} $design={design} $checked={checked}>
        <label>
          <Field type="checkbox" name={name} id={id} value={value} onChange={onChange} checked={checked} disabled={disabled} />
          <CustomCheckbox checked={checked} design={design} />
          <Typography $variant={TextVariant.BodyRegularMD}>{labelText}</Typography>
          {tooltip ? renderTooltip() : null}
        </label>
      </CheckboxWrap>
      {checked && redFlag && (
        <DidYouKnow
          text={redFlag}
          headingText={t('questionnaire.attention-required') || ''}
          icon={<ExclamationPurpleIcon />}
          borderColor={Colors.ecru}
          background={Colors.ecru50}
        />
      )}
      {showError ? <ErrorMessage name={name} render={(msg) => <FormFieldError message={msg} style={{ marginBottom: 10 }} />} /> : null}
    </Container>
  );
};

export default Checkbox;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

const CheckboxWrap = styled.div<{ $disabled?: boolean; $design?: CheckboxDesign; $checked?: boolean }>`
  opacity: ${(props) => (props.$disabled ? 0.3 : 1)};
  pointer-events: ${(props) => (props.$disabled ? 'none' : 'auto')};

  ${(props) => {
    switch (props.$design) {
      case CheckboxDesign.PinkHighlight:
        return css`
          border-radius: 4px;
          border: 1px solid transparent;
          ${props.$checked &&
          css`
            background: ${Colors.lilac}05;
            border-color: ${Colors.lilac};
            color: ${Colors.lilac};
          `}
        `;
      case CheckboxDesign.Highlight:
        return css`
          background: ${Colors.ecru50};
          border-radius: 2px 2px 0 0;
        `;
      default:
        return css``;
    }
  }}

  label {
    display: flex;
    align-items: center;
    gap: 15px;
    cursor: pointer;
    justify-content: space-between;

    ${(props) => {
      switch (props.$design) {
        case CheckboxDesign.Highlight:
          return css`
            padding: 20px 30px;
          `;
        case CheckboxDesign.PinkHighlight:
        default:
          return css`
            padding: 8px;
          `;
      }
    }}
  }

  input[type='checkbox'] {
    opacity: 0;
    position: absolute;
    z-index: -1;
  }

  p {
    flex-grow: 1;
    color: inherit;
  }
`;

const TooltipWrap = styled.div`
  display: flex;
  align-items: center;
`;
